import {StackScreenProps} from '@react-navigation/stack';
import {Auth} from 'aws-amplify';
import {formatIncompletePhoneNumber} from 'libphonenumber-js';
import {Box, Button, FormControl, Text, VStack, WarningOutlineIcon} from 'native-base';
import React, {useContext, useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {ScrollView} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {t} from 'react-native-tailwindcss';
import {Input} from '../components/Input';
import {KeyboardAvoidingView} from '../components/KeyboardAvoidingView';
import {MoneiWalletLogo} from '../components/svg/MoneiWalletLogo';
import {AuthContext} from '../lib/authContext';
import i18n from '../lib/i18n';
import {AuthNav, AuthNavParams} from './navigation';

const CODE_LENGTH = 6;

export const ConfirmLogInScreen: React.FC<
  StackScreenProps<AuthNavParams, AuthNav.ConfirmLogInScreen>
> = ({navigation}) => {
  const {
    control,
    handleSubmit,
    setError,
    formState: {errors}
  } = useForm({defaultValues: {code: ''}});
  const [isLoading, setLoading] = useState(false);
  const {currentUser, setAuthState} = useContext(AuthContext);

  useEffect(() => {
    if (!currentUser?.challengeName) {
      navigation.navigate(AuthNav.LogInScreen);
    }
  }, [currentUser?.challengeName]);

  if (!currentUser) {
    return null;
  }

  const onSubmit = async ({code}: any) => {
    setLoading(true);
    try {
      await Auth.sendCustomChallengeAnswer(currentUser, code);
      const user = await Auth.currentAuthenticatedUser();
      setAuthState({currentUser: user, isAuthenticated: true});
    } catch (error: any) {
      let errorMessage = error.message?.replace(
        'VerifyAuthChallengeResponse failed with error ',
        ''
      );
      if (error.code === 'NotAuthorizedException') {
        errorMessage = i18n.t('invalidCode');
      }
      setError('code', {type: 'manual', message: errorMessage});
      setLoading(false);
    }
  };

  const phoneNumber = formatIncompletePhoneNumber(currentUser?.getUsername() || '');

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <KeyboardAvoidingView>
        <ScrollView
          contentContainerStyle={[t.flex, t.flex1, t.flexCol, t.justifyCenter]}
          keyboardShouldPersistTaps="handled">
          <Box style={[t.m3, t.mT32, {alignItems: 'center', justifyContent: 'center'}]}>
            <MoneiWalletLogo width="250" height="50" style={[t.m4]} />
          </Box>
          <Box style={[t.pX6, t.flex1]}>
            <Text style={[t.textCenter, t.mB4, t.textGray800]}>
              {i18n.t('codeDescription', {codeLength: CODE_LENGTH})}
              <Text style={{fontWeight: 'bold'}} numberOfLines={1}>
                {phoneNumber}
              </Text>
            </Text>
            <VStack space={3}>
              <FormControl isRequired isInvalid={'code' in errors}>
                <Controller
                  name="code"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: i18n.t('codeRequired'),
                    pattern: {
                      value: new RegExp(`^[0-9]{${CODE_LENGTH}}$`),
                      message: i18n.t('invalidCode')
                    }
                  }}
                  render={({field: {onChange, onBlur, value}}) => (
                    <Input
                      autoFocus
                      value={value}
                      onBlur={onBlur}
                      autoCorrect={false}
                      autoCapitalize="none"
                      onChangeText={(text) => {
                        if (text.length > CODE_LENGTH) return;
                        onChange(text);
                      }}
                      onSubmitEditing={handleSubmit(onSubmit)}
                      placeholder={i18n.t('verificationCode')}
                      textContentType="oneTimeCode"
                      keyboardType="number-pad"
                    />
                  )}
                />
                <FormControl.ErrorMessage leftIcon={<WarningOutlineIcon size="xs" />}>
                  {errors?.code?.message}
                </FormControl.ErrorMessage>
              </FormControl>
              <Button
                variant="link"
                onPress={() => navigation.navigate(AuthNav.LogInScreen)}
                color={t.textPurple500}>
                {i18n.t('backToLogin')}
              </Button>
            </VStack>
          </Box>
        </ScrollView>
        <Box style={[t.p6]}>
          <Button isLoading={isLoading} onPress={handleSubmit(onSubmit)} style={[t.rounded]}>
            {i18n.t('confirm')}
          </Button>
        </Box>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};
